import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Ban, Loader2, MessageSquare, Plus, Trash2, Wallet, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useStore } from '../../../../context/StoreContext';
import useAccess from '../../../../hooks/useAccess';
import { SMIIFY_API } from '../../../../config/api';

// WhatsApp settings: the line in use, the wallet, and the STOP list (no marketing to these numbers).
const inr = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const card = { background: '#fff', border: '1px solid #e9e9ec', borderRadius: 14, padding: 18, display: 'flex', flexDirection: 'column', gap: 10 };
const btn = { display: 'inline-flex', alignItems: 'center', gap: 6, height: 34, padding: '0 12px', borderRadius: 9, border: '1px solid #dedee2', background: '#fff', font: 'inherit', fontSize: 12.5, fontWeight: 800, cursor: 'pointer' };

export const MarketingSettings = () => {
  const navigate = useNavigate();
  const { activeStore } = useStore();
  const { can, isOwner } = useAccess();
  const isHQ = !activeStore || activeStore?.isHQ === true || (activeStore?._id || activeStore?.id) === 'global_hq';
  const storeId = isHQ ? 'global_hq' : String(activeStore?._id || activeStore?.id);
  const canEdit = isOwner || can('marketing.edit_settings');
  const [w, setW] = useState(null);
  const [stops, setStops] = useState([]);
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);

  const h = useCallback((json) => ({ Authorization: `Bearer ${localStorage.getItem('smiify_token')}`, 'x-store-id': storeId, ...(json ? { 'Content-Type': 'application/json' } : {}) }), [storeId]);
  const load = useCallback(async () => {
    setLoading(true);
    const [a, b] = await Promise.all([
      fetch(`${SMIIFY_API}/wallet/balance`, { headers: h() }).then((r) => r.json()).catch(() => ({})),
      fetch(`${SMIIFY_API}/campaigns/optouts`, { headers: h() }).then((r) => r.json()).catch(() => ({})),
    ]);
    setW(a.success ? a.data : null);
    setStops(b.success ? b.data : []);
    setLoading(false);
  }, [h]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    const j = await fetch(`${SMIIFY_API}/campaigns/optouts`, { method: 'POST', headers: h(true), body: JSON.stringify({ phone }) }).then((r) => r.json()).catch(() => ({}));
    setMsg({ ok: j.success, text: j.message || 'Could not add.' });
    if (j.success) { setPhone(''); load(); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Allow marketing to ${p} again? Only do this if the client asked for it.`)) return;
    const j = await fetch(`${SMIIFY_API}/campaigns/optouts/${p}`, { method: 'DELETE', headers: h() }).then((r) => r.json()).catch(() => ({}));
    setMsg({ ok: j.success, text: j.message || 'Could not remove.' });
    if (j.success) load();
  };

  return (
    <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14, fontFamily: 'Nunito, sans-serif', color: '#18181a', fontSize: 13 }}>
      <h2 style={{ margin: 0, fontSize: 18, fontWeight: 900 }}>WhatsApp settings</h2>
      {msg && <div style={{ padding: '10px 14px', borderRadius: 10, fontWeight: 700, background: msg.ok ? '#f1f9f3' : '#fff4f3', color: msg.ok ? '#007a49' : '#be1025', display: 'flex', gap: 8, alignItems: 'center' }}>{msg.ok ? <CheckCircle2 size={15} /> : <AlertCircle size={15} />} {msg.text}</div>}
      {loading ? <Loader2 size={22} style={{ animation: 'spin 1s linear infinite', color: '#673de6' }} /> : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 14 }}>
            <div style={card}>
              <strong style={{ display: 'flex', gap: 8, alignItems: 'center' }}><MessageSquare size={16} color="#673de6" /> WhatsApp line</strong>
              {w?.line ? <span>{w.line.label}. Marketing {w.line.prices.MARKETING.total ? inr(w.line.prices.MARKETING.total) : 'free'} per message; bills {w.line.prices.UTILITY.total ? inr(w.line.prices.UTILITY.total) : 'free'}.</span> : <span style={{ color: '#be1025', fontWeight: 700 }}>No line is ready yet.</span>}
              <button type="button" style={btn} onClick={() => navigate('/integrations/whatsapp')}>Change line</button>
            </div>
            <div style={card}>
              <strong style={{ display: 'flex', gap: 8, alignItems: 'center' }}><Wallet size={16} color="#673de6" /> Wallet</strong>
              <span>Available <b>{inr(w?.available)}</b>{w?.held ? `, ${inr(w.held)} reserved for campaigns` : ''}.</span>
              <button type="button" style={btn} onClick={() => navigate('/wallet-billing')}>Open wallet</button>
            </div>
          </div>
          <div style={card}>
            <strong style={{ display: 'flex', gap: 8, alignItems: 'center' }}><Ban size={16} color="#be1025" /> STOP list ({stops.length})</strong>
            <span style={{ color: '#58585e' }}>Clients who replied STOP get no marketing messages from any branch. Bills and reminders still reach them.</span>
            {canEdit && (
              <div style={{ display: 'flex', gap: 8 }}>
                <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Add a number, e.g. 9876543210" inputMode="tel" style={{ flex: 1, maxWidth: 280, height: 34, padding: '0 10px', borderRadius: 9, border: '1px solid #dedee2', font: 'inherit', fontWeight: 700 }} />
                <button type="button" style={btn} onClick={add} disabled={phone.replace(/\D/g, '').length < 10}><Plus size={14} /> Add</button>
              </div>
            )}
            <div style={{ maxHeight: 360, overflowY: 'auto', borderTop: '1px solid #f0f0f2' }}>
              {stops.length === 0 ? <p style={{ color: '#797980' }}>Nobody has said STOP yet.</p> : stops.map((s) => (
                <div key={s.phone} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px dashed #e9e9ec' }}>
                  <b style={{ flex: 1 }}>+{s.phone}</b>
                  <span style={{ fontSize: 11.5, color: '#797980' }}>{s.source === 'REPLY' ? `Replied "${s.note || 'STOP'}"` : 'Added by salon'} · {new Date(s.createdAt).toLocaleDateString('en-IN')}</span>
                  {canEdit && <button type="button" onClick={() => remove(s.phone)} style={{ ...btn, height: 28, color: '#be1025' }} aria-label="Remove"><Trash2 size={13} /></button>}
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MarketingSettings;
